import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { AUTH_QUERY_KEY, useAuthCheck, useAuthConfig } from "@/app/use-auth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FadeIn } from "@/components/ui/motion";
import { apiPost } from "@/lib/api-client";

const loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  turnstileToken: z.string().optional(),
});

type LoginForm = z.infer<typeof loginSchema>;

// render=explicit defers widget rendering to our window.turnstile.render call
// (the script would otherwise auto-scan the DOM for .cf-turnstile containers).
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Allow a later retry (e.g. after a transient CDN failure).
        turnstileScriptPromise = null;
        script.remove();
        reject(new Error("Failed to load the Turnstile script"));
      };
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
}

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authQuery = useAuthCheck();
  const authConfigQuery = useAuthConfig();

  const turnstileSiteKey = authConfigQuery.data?.turnstile_site_key ?? null;
  const turnstileRequired = authConfigQuery.data?.turnstile_required === true;

  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [widgetFailed, setWidgetFailed] = useState(false);

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
      turnstileToken: "",
    },
  });

  useEffect(() => {
    if (!turnstileSiteKey) {
      return;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || widgetIdRef.current !== null) {
          return;
        }
        const container = widgetContainerRef.current;
        if (!window.turnstile || !container) {
          setWidgetFailed(true);
          return;
        }
        widgetIdRef.current = window.turnstile.render(container, {
          sitekey: turnstileSiteKey,
          callback: (token) => form.setValue("turnstileToken", token),
          // Tokens are one-time values with a short TTL — never keep a stale one.
          "expired-callback": () => form.setValue("turnstileToken", ""),
          "error-callback": () => form.setValue("turnstileToken", ""),
          theme: "auto",
        });
        setWidgetFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setWidgetFailed(true);
        }
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey, form]);

  const loginMutation = useMutation({
    mutationFn: (values: LoginForm) =>
      apiPost("/api/auth/login", {
        username: values.username,
        password: values.password,
        turnstile_token: values.turnstileToken || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      navigate("/", { replace: true });
    },
    onError: () => {
      // A failed login consumes the one-time token server-side — issue a
      // fresh challenge so the retry can pass.
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        form.setValue("turnstileToken", "");
      }
    },
  });

  useEffect(() => {
    if (authQuery.data?.authenticated) {
      navigate("/", { replace: true });
    }
  }, [authQuery.data?.authenticated, navigate]);

  const widgetActive = Boolean(turnstileSiteKey) && !widgetFailed;
  // Manual token entry stays available whenever the widget cannot render
  // (local/dev without a site key, config fetch failure, CDN failure).
  const showManualTokenInput = !widgetActive;

  return (
    <main className="grid min-h-screen place-items-center p-4">
      <FadeIn>
        <Card className="w-[min(28rem,100%)] shadow-[0_20px_40px_-20px_#000,0_0_0_1px_theme(colors.brand.500/10%)]">
          <CardHeader>
            <CardTitle className="text-2xl">Neural Net Ninja</CardTitle>
            <CardDescription>
              Sign in to access the trading dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              aria-label="Sign in"
              onSubmit={form.handleSubmit((values) => loginMutation.mutate(values))}
            >
              <div className="space-y-1.5">
                <Label htmlFor="username">Username</Label>
                <Input id="username" {...form.register("username")} />
                {form.formState.errors.username ? (
                  <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                    {form.formState.errors.username.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  {...form.register("password")}
                />
                {form.formState.errors.password ? (
                  <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>

              {widgetActive ? (
                <div className="space-y-1.5">
                  <Label>Security check</Label>
                  <div ref={widgetContainerRef} />
                </div>
              ) : null}

              {showManualTokenInput ? (
                <div className="space-y-1.5">
                  <Label htmlFor="turnstileToken">
                    Turnstile token (optional for local/dev)
                  </Label>
                  <Input
                    id="turnstileToken"
                    {...form.register("turnstileToken")}
                  />
                </div>
              ) : null}

              {turnstileRequired && !widgetActive ? (
                <p className="text-xs font-medium text-amber-500 dark:text-amber-400">
                  The server requires a security check, but the verification
                  widget could not be loaded — sign-in may fail without a valid
                  token.
                </p>
              ) : null}

              {loginMutation.error ? (
                <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-200">
                  {loginMutation.error instanceof Error
                    ? loginMutation.error.message
                    : "Login failed"}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                {loginMutation.isPending ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </FadeIn>
    </main>
  );
}

export default LoginPage;
